import React, { createContext, useCallback, useState, useContext } from "react";
import { uuid } from "uuidv4";

import ToastContainer from "../components/ToastContainer/index";

export interface IToastMessage {
    id: string;
    type?: "success" | "error" | "info";
    title: string;
    description?: string;
}

interface IToastContextData {
    addToast(message: Omit<IToastMessage, "id">): void;
    removeToast(id: string): void;
}

const ToastContext = createContext<IToastContextData>({} as IToastContextData);

const ToastProvider: React.FC = ({ children }) => {
    const [messages, setMessages] = useState<IToastMessage[]>([]);

    const addToast = useCallback(
        ({ type, title, description }: Omit<IToastMessage, "id">) => {
            const id = uuid();

            const toast = {
                id,
                type,
                title,
                description,
            };

            setMessages(state => [...state, toast]);
        },
        [],
    );
    const removeToast = useCallback((id: string) => {
        setMessages(state => state.filter(message => message.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ addToast, removeToast }}>
            {children}
            <ToastContainer messages={messages} />
        </ToastContext.Provider>
    );
};

function useToast(): IToastContextData {
    const context = useContext(ToastContext);

    if (!context) {
        throw new Error("useToast must be used within a ToastProvider");
    }

    return context;
}

export { ToastProvider, useToast };
